import { type FC, useEffect, useRef, useState } from 'react';

import { animated, useTransition } from '@react-spring/web';
import WebApp from '@twa-dev/sdk';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';

import { usePlayGame } from '@/entities/game/hooks/use-play-game';
import { GameIcon } from '@/entities/game/ui/game-icon';
import { useUser } from '@/entities/user/model/user.store';

import { getIsSlowDevice } from '@/shared/helpers/get-is-slow-device';
import { useMeasure } from '@/shared/hooks/use-measure';
import {
  useGamesActions,
  useGamesByCategory,
} from '@/shared/store/games-store/games-store';
import { CATEGORY } from '@/shared/store/games-store/types';
import { Button, ButtonSize } from '@/shared/ui/button';

import { ReactComponent as LabelNew } from '@/assets/images/new.svg?react';

import s from './GameBar.module.scss';

interface GameBarProps {
  className?: string;
}

export const GameBar: FC<GameBarProps> = ({ className }) => {
  const { t } = useTranslation();
  const isSlowDevice = getIsSlowDevice();

  const { content: recentGames = [] } = useGamesByCategory(CATEGORY.RECENT) ?? {};

  const user = useUser();

  const playGame = usePlayGame();

  const [isLoading, setIsLoading] = useState(true);

  const { fetchGamesByCategory } = useGamesActions();

  const hasRecentGame = recentGames.length > 0;

  const transitions = useTransition(!isLoading, {
    from: { y: '50%' },
    enter: { y: '0%' },
    leave: { y: '50%' },
    immediate: isSlowDevice,
  });

  useEffect(() => {
    if (!hasRecentGame) {
      void fetchGamesByCategory(0, CATEGORY.RECENT).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ref = useRef<HTMLDivElement>(null);

  const { clientHeight } = useMeasure(ref, [WebApp.viewportStableHeight]);

  useEffect(() => {
    document.documentElement.style.setProperty('--gamebar-height', `${clientHeight}px`);

    return () => {
      document.documentElement.style.removeProperty('--gamebar-height');
    };
  }, [clientHeight]);

  if (isLoading) return null;

  const game = hasRecentGame ? recentGames[0] : user?.gameOfTheDay;

  if (!game) return null;

  const { title, flags } = game;
  const { new: isNewGame } = flags;

  return transitions(
    (styles, item) =>
      item && (
        <animated.div style={styles} className={clsx(s.container, className)}>
          <div ref={ref} className={s['static-wrapper']}>
            <div className={s['icon-container']}>
              <GameIcon className={s.icon} game={game} />
              {isNewGame && <LabelNew className={s['lable-new']} />}
            </div>
            <div className={s['texts-wrapper']}>
              <span className={s.label}>
                {hasRecentGame ? t('Play again') : t('Game of The Day')}
              </span>
              <span className={s.title}>{title}</span>
            </div>
            <Button
              onClick={() => void playGame(game, 'game_bar')}
              className={s.button}
              size={ButtonSize.s30}
            >
              {t('Play')}
            </Button>
          </div>
        </animated.div>
      )
  );
};
